import React from 'react';
import { X } from 'lucide-react';
import { useTuitionContext } from '../context/TuitionContext';
import { GaugeComponent } from 'react-gauge-component';

const SidePanel = () => {
    const {
        // State
        profitPercentage,
        showPanel,
        showInstructions,
        hasCalculated,
        setShowInstructions,
        setShowPanel,

        // Calculation methods
        calculateCurrentProfitMargin,

        // Utility methods
        formatCurrency,
        getSpeedometerColor,

        // Computed values
        optimumTuition,
        currentTuitionNum,
    } = useTuitionContext();

    const TargetTuition = () => (
        <>
            <div className='border border-1 border-dark rounded-medium-left rounded-medium-right'>
                <h1 className='display-big text-center text-teal fw-bold pt-4 pb-4'>
                    {formatCurrency(optimumTuition)}
                </h1>
            </div>
            <div className='border border-1 border-dark rounded-medium-left rounded-medium-right mt-3'>
                <div className="mb-2 text-dark">
                    <GaugeComponent
                        labels={{
                            valueLabel: {
                                style: {
                                    fill: getSpeedometerColor(calculateCurrentProfitMargin()),
                                    outline: 'none',
                                    textShadow: 'none',
                                }
                            }
                        }}
                        arc={{
                            subArcs: [
                                {
                                    limit: 20,
                                    color: '#EA4228',
                                    showTick: true
                                },
                                {
                                    limit: 40,
                                    color: '#F58B19',
                                    showTick: true
                                },
                                {
                                    limit: 50,
                                    color: '#F5CD19',
                                    showTick: true
                                },
                                {
                                    limit: 60,
                                    color: '#5BE12C',
                                    showTick: true
                                },
                                {
                                    limit: 100,
                                    color: '#017000',
                                    showTick: true
                                },
                            ]
                        }}
                        pointer={{ elastic: true, animation: { duration: 500 } }}

                        value={calculateCurrentProfitMargin().toFixed(0)}
                    />
                </div>
                <div className='p-4 text-center' style={{ fontWeight: 300 }}>
                    Your Center requires a <span className='text-teal fw-bold'>{formatCurrency(optimumTuition)}</span> tuition rate in order to
                    achieve a <span className='text-teal fw-bold'>{profitPercentage}%</span> profit margin based on your current
                    level of expenses and revenue. At your current
                    tuition rate of <span className='text-dark fw-bold'>{formatCurrency(currentTuitionNum)}</span>,
                    your profit margin is <span className='text-dark fw-bold'>{calculateCurrentProfitMargin().toFixed(0)}%</span>.
                </div>
            </div>

            <InstructionToggle />
        </>
    )

    const Instructions = () => (
        <div className='p-4'>
            <h5 className='text-teal instructionHeader'>How to use the Tuition Rate Calculator</h5>
            <p>The Target Tuition Rate Calculator helps you find the optimal tuition rate for your center based on your expenses and desired profit margin.</p>
            <ol className='text-start'>
                <li>Enter your center's <strong class='text-uppercase'>Current Enrollment</strong>  and <strong class='text-uppercase'>Current Tuition Rate</strong>.</li>
                <li>Input your center's <strong class='text-uppercase'>Overhead &amp; Expenses</strong>. The most common expenses are already included, but you can add custom expenses as needed.</li>
                <li>Review the calculated <strong class='text-uppercase'>Total Overhead</strong>.</li>
                <li>Set your desired <strong class='text-uppercase'>Profit Margin</strong>.</li>
                <li>Click <strong class='text-uppercase'>Calculate</strong> to see the optimum tuition rate.</li>
                <li>Change your Tuition Information and Expenses and immediately see the results update!</li>
            </ol>

            <InstructionToggle />

            <div className="mt-3" style={{ fontSize: '0.8rem' }}>
                <p><strong className="text-uppercase">Note:</strong>
                    <em>The Tuition Rate Calculator is designed for Centers that have 60 or more students.
                        For under 50 students your calculated tuition rate may be very inflated. If you have less than 50 students,
                        your focus and priority is to increase your student enrollment. For now, in order to get to 60 students,
                        make incremental changes to your current tuition rate as you have been trained, and then when you hit 60, use
                        the Tuition Rate Calculator to determine the best tuition rate that will give you a 50% profit margin. You can
                        always insert 60 students even if you have less to determine what your tuition might be with 60 students.
                        That will help with your financial planning.

                    </em>
                </p>
            </div>
        </div>)

    const InstructionToggle = () => (
        <>
            {hasCalculated &&
                <div className='mt-4 text-center'>
                    <button className='btn btn-link btn-sm' onClick={() => setShowInstructions(!showInstructions)}>
                        {showInstructions ? (hasCalculated ? "Show Target Tuition" : "Hide Instructions") : "Show Instructions"}
                    </button>
                </div>
            }
            {!hasCalculated &&
                <div className="m-4 text-center">
                    <button className="btn btn-secondary bg-gold btn-lg btn-shadow" onClick={() => setShowPanel(false)}>
                        Get Started!
                    </button>
                </div>
            }
        </>)

    return (
        <div className={`side-panel pe-4 pe-md-0 ${showPanel ? 'show' : ''}`}>
            <div className="p-1">
                <div className="d-flex justify-content-end align-items-center mb-4 pe-2 pe-md-1">
                    <button
                        onClick={() => setShowPanel(false)}
                        className="btn btn-outline-secondary btn-sm"
                    >
                        Close <X size={20} />
                    </button>
                </div>
                {showInstructions &&
                    <div className="d-lg-none d-flex align-items-center justify-content-center">
                        <img src='/img/the-tutoring-center.png' alt='The Tutoring Center®' className="logo me-3" />
                        <h1 className="text-dark text-teal text-uppercase fw-bold title-text mt-1">
                            Target Tuition<br />
                            Rate Calculator
                        </h1>
                    </div>
                }
                {!showInstructions &&
                    <div className='d-flex p-2 align-items-center justify-content-center'>
                        <img src='/img/target.png' alt='Your Tuition Target' className='me-2' />
                        <h1 className="mb-0 text-teal fw-bold text-uppercase">{showInstructions ? "Instructions" : "Target Tuition"}</h1>
                    </div>
                }

                <div className='p-1 ps-md-3 pe-md-3'>
                    {showInstructions && <Instructions />}
                    {!showInstructions && <TargetTuition />}
                </div>
            </div>
        </div>
    );
};

export default SidePanel;
